import { DataStore } from 'aws-amplify';
import { AdminActionHistory } from '../models';

export enum AdminActionType {
  // Manifest Actions
  MANIFEST_STATUS_CHANGE = 'Manifest Status Change',
  MANIFEST_DELETE = 'Manifest Delete',
  MANIFEST_COMPLETE = 'Manifest Complete',

  // SSCC Actions
  SSCC_ADD_TO_MANIFEST = 'SSCC Add to Manifest',
  SSCC_REMOVE_FROM_MANIFEST = 'SSCC Remove from Manifest',

  // ASN Actions
  ASN_REMOVE_FROM_SSCC = 'ASN Remove from SSCC',
  ASN_ASSIGN_TO_SSCC = 'ASN Assign to SSCC',
  ASN_UNSTAGE = 'ASN Unstage',
  ASN_DELETE = 'ASN Delete',

  // SSCC Actions (Admin)
  SSCC_COMPLETE = 'SSCC Complete'
}

interface AuditLogOptions {
  actionType: AdminActionType;
  beforeAction: any;
  afterAction: any;
  actionBy: string;
  reason?: string;
}

/**
 * Logs an admin action to the AdminActionHistory table
 * @param options - The audit log options
 * @returns Promise<AdminActionHistory> - The created audit log entry
 */
export async function logAdminAction(options: AuditLogOptions): Promise<AdminActionHistory> {
  const { actionType, beforeAction, afterAction, actionBy, reason } = options;

  try {
    // Serialize the before and after states
    const beforeActionStr = JSON.stringify(beforeAction, null, 2);
    const afterActionStr = JSON.stringify(
      {
        ...afterAction,
        ...(reason && { reason })
      },
      null,
      2
    );

    const auditLog = await DataStore.save(
      new AdminActionHistory({
        actionType,
        beforeAction: beforeActionStr,
        afterAction: afterActionStr,
        actionBy,
        actionTime: new Date().toISOString()
      })
    );

    console.log('Admin action logged:', {
      id: auditLog.id,
      actionType,
      actionBy,
      actionTime: auditLog.actionTime
    });

    return auditLog;
  } catch (error) {
    console.error('Failed to log admin action:', error);
    throw new Error(`Audit logging failed: ${error}`);
  }
}

/**
 * Gets audit logs with optional filters
 * @param filters - Optional filters for querying logs
 * @returns Promise<AdminActionHistory[]> - Array of audit logs
 */
export async function getAuditLogs(filters?: {
  actionType?: AdminActionType;
  actionBy?: string;
  startDate?: Date;
  endDate?: Date;
}): Promise<AdminActionHistory[]> {
  try {
    console.log('getAuditLogs called with filters:', filters);

    // Check DataStore sync status
    const syncStatus = await DataStore.observe(AdminActionHistory).subscribe({
      next: () => console.log('AdminActionHistory DataStore observer active')
    });

    let logs: AdminActionHistory[];

    if (filters?.actionType) {
      console.log('Querying with actionType filter:', filters.actionType);
      logs = await DataStore.query(AdminActionHistory, (c) => c.actionType.eq(filters.actionType as string));
    } else {
      console.log('Querying all AdminActionHistory records from DataStore...');
      logs = await DataStore.query(AdminActionHistory);
    }

    console.log('DataStore query returned:', logs.length, 'records');
    console.log('Sample of returned records:', logs.slice(0, 3));

    // Apply additional filters in memory
    let filteredLogs = logs;

    if (filters?.actionBy) {
      filteredLogs = filteredLogs.filter((log) => log.actionBy === filters.actionBy);
    }

    if (filters?.startDate) {
      filteredLogs = filteredLogs.filter((log) => log.actionTime && new Date(log.actionTime) >= filters.startDate!);
    }

    if (filters?.endDate) {
      filteredLogs = filteredLogs.filter((log) => log.actionTime && new Date(log.actionTime) <= filters.endDate!);
    }

    // Sort by most recent first
    return filteredLogs.sort((a, b) => {
      const dateA = a.actionTime ? new Date(a.actionTime).getTime() : 0;
      const dateB = b.actionTime ? new Date(b.actionTime).getTime() : 0;
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Failed to fetch audit logs:', error);
    throw new Error(`Failed to fetch audit logs: ${error}`);
  }
}

/**
 * Formats an audit log entry for display
 * @param log - The audit log entry
 * @returns Formatted audit log object
 */
export function formatAuditLog(log: AdminActionHistory) {
  try {
    const beforeAction = log.beforeAction ? JSON.parse(log.beforeAction) : null;
    const afterActionParsed = log.afterAction ? JSON.parse(log.afterAction) : null;

    return {
      id: log.id,
      timestamp: log.actionTime,
      user: log.actionBy,
      action: log.actionType,
      before: beforeAction,
      after: afterActionParsed,
      reason: afterActionParsed?.reason,
      changes: generateChangesSummary(beforeAction, afterActionParsed)
    };
  } catch (error) {
    console.error('Failed to format audit log:', error);
    return {
      id: log.id,
      timestamp: log.actionTime,
      user: log.actionBy,
      action: log.actionType,
      before: null,
      after: null,
      reason: null,
      changes: 'Error parsing log data'
    };
  }
}

/**
 * Generates a human-readable summary of changes
 */
function generateChangesSummary(before: any, after: any): string {
  if (!before && !after) return 'No changes recorded';

  const changes: string[] = [];

  // Handle ASN Delete action (bulk removal)
  if (after?.asnsRemovedList || after?.asnsRemovedCount || before?.asnsToBeRemoved) {
    const removedCount = after?.asnsRemovedCount || before?.asnsToBeRemoved?.length || 0;
    const removedList = after?.asnsRemovedList || before?.asnsToBeRemoved?.map((a: any) => a.asn) || [];
    const ssccId = before?.ssccId || after?.ssccId || 'Unknown';
    const destination = before?.ssccDestination || after?.ssccDestination || '';

    if (removedCount > 0) {
      const asnDisplay =
        removedList.length <= 3
          ? removedList.join(', ')
          : `${removedList.slice(0, 3).join(', ')} +${removedList.length - 3} more`;
      changes.push(`Removed ${removedCount} ASN(s) from SSCC ${ssccId}`);
      changes.push(`ASNs: ${asnDisplay}`);
      if (destination) {
        changes.push(`Destination: ${destination}`);
      }
      if (after?.remainingAsnsCount !== undefined) {
        changes.push(`Remaining ASNs: ${after.remainingAsnsCount}`);
      }
    }
    return changes.length > 0 ? changes.join('; ') : 'ASNs removed from SSCC';
  }

  // Handle status changes
  if (before?.status && after?.status && before.status !== after.status) {
    changes.push(`Status: ${before.status} → ${after.status}`);
  }

  // Handle SSCC additions/removals
  if (before?.ssccs && after?.ssccs) {
    const beforeSSCCs = new Set(before.ssccs);
    const afterSSCCs = new Set(after.ssccs);

    const added = [...afterSSCCs].filter((x) => !beforeSSCCs.has(x));
    const removed = [...beforeSSCCs].filter((x) => !afterSSCCs.has(x));

    if (added.length > 0) {
      changes.push(`Added SSCCs: ${added.join(', ')}`);
    }
    if (removed.length > 0) {
      changes.push(`Removed SSCCs: ${removed.join(', ')}`);
    }
  }

  // Handle ASN changes (single ASN operations)
  if (before?.asn && after?.asn && before.asn !== after.asn) {
    changes.push(`ASN: ${before.asn} → ${after.asn}`);
  }

  // Handle ASN assignment to SSCC
  if (after?.targetSSCC && before?.sourceSSCC) {
    changes.push(`Moved ASN ${before.asn || 'N/A'} from ${before.sourceSSCC} to ${after.targetSSCC}`);
  }

  // Handle ASN unstage (priority shipping)
  if (after?.status === 'available' && before?.status && before.status !== 'available') {
    changes.push(`ASN ${before.asn || 'N/A'} marked as available for priority shipping`);
  }

  // Handle manifest assignments
  if (before?.manifestId !== after?.manifestId) {
    if (after?.manifestId) {
      changes.push(`Added to Manifest: ${after.manifestId}`);
    } else if (before?.manifestId) {
      changes.push(`Removed from Manifest: ${before.manifestId}`);
    }
  }

  // Handle SSCC Complete
  if (after?.completedAt || (after?.status === 'Delivered' && before?.status !== 'Delivered')) {
    const ssccId = before?.ssccId || after?.ssccId || 'Unknown';
    changes.push(`SSCC ${ssccId} marked as completed/delivered`);
    if (after?.asnsUpdatedInCoupa) {
      changes.push(`${after.asnsUpdatedInCoupa} ASN(s) updated to Available in Coupa`);
    }
  }

  return changes.length > 0 ? changes.join('; ') : 'State changed';
}
